import React from "react";
import { Trans } from "@lingui/react/macro";
import { usePendingChangeRequestQuery } from "../../api/bookingChangeRequestApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import { Link } from "react-router";
import { ArrowRight, Clock, Mail } from "lucide-react";
import { format } from "date-fns";
import ChangeRequestExpiry from "./ChangeRequestExpiry.tsx";

interface ChangeRequestBannerProps {
  bookingId: string;
  currentUserId: string;
}

const ChangeRequestBanner: React.FC<ChangeRequestBannerProps> = ({
  bookingId,
  currentUserId,
}) => {
  const { data: changeRequest, isLoading } =
    usePendingChangeRequestQuery(bookingId);
  const { dateFnsLocale } = useLanguageStore();

  if (isLoading || !changeRequest) {
    return null;
  }

  const isRequester = changeRequest.requestedBy === currentUserId;

  const formatTimeslot = (from: string, to: string): string =>
    `${format(new Date(from), "EEE MMM d, HH:mm", { locale: dateFnsLocale })} - ${format(new Date(to), "HH:mm", { locale: dateFnsLocale })}`;

  const timeslots = (
    <div className="flex flex-col gap-1">
      {changeRequest.timeslotChanges.map((change, index) => (
        <div
          key={index}
          className="flex flex-col gap-0.5 text-[11px] sm:flex-row sm:items-center sm:gap-1.5"
        >
          <span className="line-through opacity-70">
            {formatTimeslot(change.originalFrom, change.originalTo)}
          </span>
          <ArrowRight className="hidden h-3 w-3 shrink-0 sm:block" />
          <span className="font-medium">
            {formatTimeslot(change.proposedFrom, change.proposedTo)}
          </span>
        </div>
      ))}
    </div>
  );

  // Requester view: informational, not actionable.
  if (isRequester) {
    return (
      <div className="flex flex-col gap-1.5 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2 text-yellow-700">
        <div className="inline-flex items-center gap-1.5 text-xs font-medium">
          <Clock className="h-3 w-3 shrink-0" />
          <Trans>Move pending</Trans>
        </div>
        {timeslots}
        <ChangeRequestExpiry autoCancelAt={changeRequest.autoCancelAt} />
      </div>
    );
  }

  // Responder view: links to the booking detail to review and respond.
  return (
    <Link
      to={`/user/${currentUserId}/bookings/${bookingId}`}
      className="flex flex-col gap-1.5 rounded-lg border border-amber-300 bg-amber-100 px-3 py-2 text-amber-800 transition-colors hover:bg-amber-200"
    >
      <div className="inline-flex items-center gap-1.5 text-xs font-medium">
        <Mail className="h-3 w-3 shrink-0" />
        <Trans>Move requested — review</Trans>
      </div>
      {timeslots}
      <ChangeRequestExpiry autoCancelAt={changeRequest.autoCancelAt} />
    </Link>
  );
};

export default ChangeRequestBanner;
